import { normalizeKindId, slugify, type Adventure } from '@gm-binder/shared';

/**
 * Maps a declarative kind id to its backing list inside the in-memory Adventure object, so the
 * editor can do entity CRUD with the whole-object autosave (the server re-normalizes on PUT).
 *
 * Built-in kinds use their named buckets; custom kinds live under `entities[kind]`.
 */
export type Entity = Record<string, unknown> & { id?: string };

/** Read the entity list for a kind without mutating the adventure (returns [] when absent). */
export function listEntitiesOf(adv: Adventure, kind: string): Entity[] {
  const k = normalizeKindId(kind);
  switch (k) {
    case 'place':
      return (adv.rooms?.rooms ?? []) as Entity[];
    case 'character':
      return adv.characters as Entity[];
    case 'npc':
      return adv.npcs as Entity[];
    case 'beast':
      return adv.monsters as Entity[];
    case 'encounter':
      return adv.encounters as Entity[];
    case 'clue':
      return adv.clues as Entity[];
    case 'item':
      return adv.items as Entity[];
    case 'page':
      return adv.pages as Entity[];
    case 'table':
      return ((adv.curses as { curses?: Entity[] } | undefined)?.curses ?? []) as Entity[];
    default:
      return (adv.entities?.[k] ?? []) as Entity[];
  }
}

/** Get (creating if needed) the mutable backing list for a kind. Mutates `adv`. */
function ensureList(adv: Adventure, kind: string): Entity[] {
  const k = normalizeKindId(kind);
  switch (k) {
    case 'place':
      adv.rooms ??= { floors: [], rooms: [] };
      return adv.rooms.rooms as Entity[];
    case 'character':
      return adv.characters as Entity[];
    case 'npc':
      return adv.npcs as Entity[];
    case 'beast':
      return adv.monsters as Entity[];
    case 'encounter':
      return adv.encounters as Entity[];
    case 'clue':
      return adv.clues as Entity[];
    case 'item':
      return adv.items as Entity[];
    case 'page':
      return adv.pages as Entity[];
    case 'table': {
      const curses = (adv.curses ??= {}) as { curses?: Entity[] };
      curses.curses ??= [];
      return curses.curses;
    }
    default:
      adv.entities ??= {};
      adv.entities[k] ??= [];
      return adv.entities[k] as Entity[];
  }
}

/** A unique id for a new entity, slugified from a base label and de-duplicated within its list. */
export function nextEntityId(list: Entity[], base: string): string {
  const root = slugify(base) || 'voce';
  const taken = new Set(list.map((e) => String(e.id ?? '')));
  if (!taken.has(root)) return root;
  let n = 2;
  while (taken.has(`${root}-${n}`)) n += 1;
  return `${root}-${n}`;
}

/** Append a new entity to a kind's list, assigning a stable id. Mutates `adv`; returns the entity. */
export function addEntity(adv: Adventure, kind: string, partial: Entity): Entity {
  const list = ensureList(adv, kind);
  const base = String(partial.name ?? partial.title ?? 'Nuova voce');
  const id = String(partial.id ?? '') || nextEntityId(list, base);
  const entity: Entity = { ...partial, id };
  list.push(entity);
  return entity;
}

/** Patch an existing entity in place. Mutates `adv`. */
export function updateEntityIn(adv: Adventure, kind: string, id: string, patch: Entity): void {
  const list = ensureList(adv, kind);
  const idx = list.findIndex((e) => String(e.id) === id);
  if (idx >= 0) list[idx] = { ...list[idx], ...patch, id: list[idx].id };
}

/** Remove an entity. Mutates `adv`. */
export function deleteEntityFrom(adv: Adventure, kind: string, id: string): void {
  const list = ensureList(adv, kind);
  const idx = list.findIndex((e) => String(e.id) === id);
  if (idx >= 0) list.splice(idx, 1);
}

/** Move an entity up/down within its list. Mutates `adv`. */
export function moveEntity(adv: Adventure, kind: string, id: string, dir: -1 | 1): void {
  const list = ensureList(adv, kind);
  const idx = list.findIndex((e) => String(e.id) === id);
  const next = idx + dir;
  if (idx < 0 || next < 0 || next >= list.length) return;
  [list[idx], list[next]] = [list[next], list[idx]];
}
